import { put, call } from 'redux-saga/effects'
import {
  operatorUserApiService,
  handleError,
  tariffService,
  privacyRuleService,
  accessGroupApiService,
  cityService,
} from 'Services/ApiService.js'
import OperatorUserActions from 'Stores/OperatorUser/Actions'
import StationActions from 'Stores/Station/Actions'
import { operatorUserLogin } from './AuthSaga'
import { toaster } from 'rsuite'
import { IFToastMessage } from 'Components'
import { encodeObjectToBase64 } from 'Utils/base64functions'
import i18n from 'i18next'

export function* fetchOperatorUserList({ filter, offset }) {
  try {
    if (filter != null) {
      filter = encodeObjectToBase64(filter)
    }
    if (offset === 0) {
      yield put(OperatorUserActions.fetchOperatorUserListLoading())
    } else yield put(OperatorUserActions.fetchOperatorUserListLoadingMore())
    const { data } = yield call(operatorUserApiService.get, '/', {
      params: {
        filter: filter,
        offset: offset,
      },
    })
    yield put(
      OperatorUserActions.fetchOperatorUserListSuccess(
        offset,
        data.operatorUsers,
        data.nextOffset,
      ),
    )
  } catch (e) {
    yield put(OperatorUserActions.fetchOperatorUserListFail(e))
    handleError(e)
  }
}

export function* updateOperatorUser({ operatorUserId, permissions }) {
  yield put(OperatorUserActions.updateOperatorUserLoading())
  try {
    const { data } = yield call(
      operatorUserApiService.patch,
      `/${operatorUserId}`,
      { permissions: permissions },
    )
    yield put(
      OperatorUserActions.updateOperatorUserSuccess(operatorUserId, data),
    )
    toaster.push(
      <IFToastMessage
        type="success"
        text={i18n.t('OperatorUserProfile.UpdateMessage')}
      />,
    )
  } catch (e) {
    yield put(OperatorUserActions.updateOperatorUserFail(e))
    handleError(e)
  }
}

export function* fetchInviteOperatorUserList({ offset }) {
  try {
    if (offset === 0)
      yield put(OperatorUserActions.fetchInviteOperatorUserListLoading())
    else yield put(OperatorUserActions.fetchInviteOperatorUserListLoadingMore())

    const { data } = yield call(
      operatorUserApiService.get,
      '/inviteOperatorUser',
      {
        params: {
          offset: offset,
        },
      },
    )
    yield put(
      OperatorUserActions.fetchInviteOperatorUserListSuccess(
        data.invitations,
        data.nextOffset,
        offset,
      ),
    )
  } catch (e) {
    yield put(OperatorUserActions.fetchInviteOperatorUserListFail(e))

    handleError(e)
  }
}

export function* inviteOperatorUser({ operatorUserData }) {
  yield put(OperatorUserActions.inviteOperatorUserLoading())
  try {
    const { data } = yield call(
      operatorUserApiService.post,
      '/inviteOperatorUser',
      { ...operatorUserData },
    )

    yield put(OperatorUserActions.inviteOperatorUserSuccess(data))
    toaster.push(
      <IFToastMessage
        type="success"
        text={i18n.t('OperatorUserProfile.InviteSuccess')}
      />,
    )
  } catch (e) {
    yield put(OperatorUserActions.inviteOperatorUserFail(e))
    handleError(e)
  }
}

export function* deleteOperatorUser({ operatorUserId, ref }) {
  yield put(OperatorUserActions.deleteOperatorUserLoading())
  try {
    const { data } = yield call(
      operatorUserApiService.delete,
      `${operatorUserId}`,
    )
    toaster.push(
      <IFToastMessage
        type="success"
        text={i18n.t('OperatorUserProfile.DeleteSuccess')}
      />,
    )

    yield put(OperatorUserActions.deleteOperatorUserSuccess(operatorUserId))

    if (ref) ref.current.collapse()
  } catch (e) {
    yield put(OperatorUserActions.deleteOperatorUserFail(e))
    handleError(e)
  }
}

export function* unblockOperatorUser({ operatorUserId }) {
  yield put(OperatorUserActions.unblockOperatorUserLoading())
  try {
    const { data } = yield call(
      operatorUserApiService.patch,
      `/${operatorUserId}/unblock`,
    )
    yield put(
      OperatorUserActions.unblockOperatorUserSuccess(operatorUserId, data),
    )
    toaster.push(
      <IFToastMessage
        type="success"
        text={i18n.t('OperatorUserProfile.UnblockSuccess')}
      />,
    )
  } catch (e) {
    yield put(OperatorUserActions.unblockOperatorUserFail(e))
    handleError(e)
  }
}

export function* deleteInviteOperatorUser({ inviteOperatorUserId }) {
  yield put(OperatorUserActions.deleteInviteOperatorUserLoading())
  try {
    const { data } = yield call(
      operatorUserApiService.delete,
      `/inviteOperatorUser/${inviteOperatorUserId}`,
    )

    yield put(
      OperatorUserActions.deleteInviteOperatorUserSuccess(inviteOperatorUserId),
    )
    toaster.push(
      <IFToastMessage
        type="success"
        text={i18n.t('OperatorUserPageContainer.InvitationDeleted')}
      />,
    )
  } catch (e) {
    yield put(OperatorUserActions.deleteInviteOperatorUserFail(e))
    handleError(e)
  }
}

export function* sendOperatorUserEmail({ email }) {
  yield put(OperatorUserActions.sendOperatorUserEmailLoading())
  try {
    const { data } = yield call(
      operatorUserApiService.post,
      `/sendOperatorUserEmail`,
      {
        email: email,
      },
    )
    yield put(OperatorUserActions.sendOperatorUserEmailSuccess())
    toaster.push(
      <IFToastMessage
        type="success"
        text={i18n.t('OperatorUserPageContainer.InvitationSent')}
      />,
    )
  } catch (e) {
    yield put(OperatorUserActions.sendOperatorUserEmailFail(e))
    handleError(e)
  }
}

export function* fetchInviteOperatorUser({ inviteOperatorUserId }) {
  yield put(OperatorUserActions.fetchInviteOperatorUserLoading())
  try {
    const { data } = yield call(
      operatorUserApiService.get,
      `/inviteOperatorUser/${inviteOperatorUserId}`,
    )

    yield put(OperatorUserActions.setInviteOperatorUserStatus(data.status))
    yield put(OperatorUserActions.fetchInviteOperatorUserSuccess(data))
  } catch (e) {
    yield put(OperatorUserActions.fetchInviteOperatorUserFail(e))
    handleError(e)
  }
}

export function* createOperatorUser({ operatorUser }) {
  yield put(OperatorUserActions.createOperatorUserLoading())
  try {
    yield call(operatorUserApiService.post, '/', {
      ...operatorUser,
    })
    yield put(OperatorUserActions.createOperatorUserSuccess())
  } catch (e) {
    yield put(OperatorUserActions.createOperatorUserFail(e))
    handleError(e)
  }
}

export function* fetchStationCities({}) {
  yield put(StationActions.fetchStationCitiesLoading())
  try {
    const { data } = yield call(cityService.get, '/')
    yield put(StationActions.fetchStationCitiesSuccess(data))
  } catch (e) {
    yield put(StationActions.fetchStationCitiesFail(e))
    handleError(e)
  }
}

export function* addStationCity({ city }) {
  yield put(StationActions.addStationCityLoading())
  try {
    const { data } = yield call(cityService.post, '/', {
      name: city,
    })

    yield put(StationActions.addStationCitySuccess(data.name))
  } catch (e) {
    yield put(StationActions.addStationCityFail(e))
    handleError(e)
  }
}

export function* fetchTariffsList({}) {
  yield put(OperatorUserActions.fetchTariffsListLoading())
  try {
    const { data } = yield call(tariffService.get, '/me/all')
    yield put(OperatorUserActions.fetchTariffsListSuccess(data.tariffs))
  } catch (e) {
    yield put(OperatorUserActions.fetchTariffsListFail(e))
    handleError(e)
  }
}

export function* fetchPrivacySchedule({}) {
  yield put(OperatorUserActions.fetchPrivacyScheduleLoading())
  try {
    const { data } = yield call(privacyRuleService.get, '/me/all')
    yield put(
      OperatorUserActions.fetchPrivacyScheduleSuccess(data.privacyRules),
    )
  } catch (e) {
    yield put(OperatorUserActions.fetchPrivacyScheduleFail(e))
    handleError(e)
  }
}

export function* fetchAccessGroupsList({}) {
  yield put(OperatorUserActions.fetchAccessGroupsListLoading())
  try {
    const { data } = yield call(accessGroupApiService.get, '/me/all')
    yield put(
      OperatorUserActions.fetchAccessGroupsListSuccess(data.accessGroups),
    )
  } catch (e) {
    yield put(OperatorUserActions.fetchAccessGroupsListFail(e))
    handleError(e)
  }
}
export function* fetchOperatorUsersCount({ filter }) {
  yield put(OperatorUserActions.fetchOperatorUsersCountLoading())
  try {
    if (filter != null) {
      filter = encodeObjectToBase64(filter)
    }

    const { data } = yield call(operatorUserApiService.get, '/count', {
      params: {
        filter: filter,
      },
    })
    yield put(OperatorUserActions.fetchOperatorUsersCountSuccess(data.count))
  } catch (e) {
    yield put(OperatorUserActions.fetchOperatorUsersCountError(e))
    handleError(e)
  }
}
export function* fetchOperatorUsersInvitationsCount({}) {
  yield put(OperatorUserActions.fetchOperatorUsersInvitationsCountLoading())
  try {
    const { data } = yield call(
      operatorUserApiService.get,
      '/inviteOperatorUser/count',
    )
    yield put(
      OperatorUserActions.fetchOperatorUsersInvitationsCountSuccess(data.count),
    )
  } catch (e) {
    yield put(OperatorUserActions.fetchOperatorUsersInvitationsCountFail(e))
    handleError(e)
  }
}

export function* changePasswordOrName({
  name,
  password,
  onResponse = () => {},
}) {
  yield put(OperatorUserActions.changePasswordOrNameLoading())
  try {
    if (password && name) {
      const { data } = yield call(operatorUserApiService.patch, `/me`, {
        name: name,
        password: password,
      })
    } else if (password && !name) {
      const { data } = yield call(operatorUserApiService.patch, `/me`, {
        password: password,
      })
    } else if (name && !password) {
      const { data } = yield call(operatorUserApiService.patch, `/me`, {
        name: name,
      })
    }
    yield put(OperatorUserActions.changePasswordOrNameSuccess())
    onResponse()
    if (!password && name) {
      toaster.push(
        <IFToastMessage
          type="success"
          text={i18n.t('OperatorUserProfile.NameChange')}
        />,
      )
    } else {
      toaster.push(
        <IFToastMessage
          type="success"
          text={i18n.t('OperatorUserProfile.PasswordChange')}
        />,
      )
    }
  } catch (e) {
    yield put(OperatorUserActions.changePasswordOrNameFail(e))
    handleError(e)
  }
}
